import type { TrainingRetryMode } from '@hcc/types';
import { SHARED_INPUT_CLASS } from './pageMeta';

/**
 * Module-level settings: title, description, whether it is marked at all,
 * pass mark, retry behaviour and the question bank.
 *
 * An unmarked module is an assessment that collects answers (DSE and the
 * like): no answer key, no score, and completing it is the whole outcome.
 * Question grids are incompatible with the bank and with try-again, so those
 * controls are disabled rather than left to fail publish validation.
 */
export function ModuleSettings({
  title,
  description,
  graded,
  passMark,
  retryMode,
  serveQuestionCount,
  shuffleQuestions,
  questionCount,
  hasQuestionGroups,
  onTitle,
  onDescription,
  onGraded,
  onPassMark,
  onRetryMode,
  onServeQuestionCount,
  onShuffleQuestions,
}: {
  title: string;
  description: string;
  /** False = collect answers only, never marked. */
  graded: boolean;
  passMark: number;
  retryMode: TrainingRetryMode;
  /** Null serves every question; a number serves that many at random. */
  serveQuestionCount: number | null;
  shuffleQuestions: boolean;
  /** Total questions authored, used to bound the subset size. */
  questionCount: number;
  /** True when any page is a question grid. */
  hasQuestionGroups: boolean;
  onTitle: (next: string) => void;
  onDescription: (next: string) => void;
  onGraded: (next: boolean) => void;
  onPassMark: (next: number) => void;
  onRetryMode: (next: TrainingRetryMode) => void;
  onServeQuestionCount: (next: number | null) => void;
  onShuffleQuestions: (next: boolean) => void;
}) {
  const subsetOn = serveQuestionCount !== null;
  const bankBlocked = hasQuestionGroups;
  return (
    <div className="rounded-token border border-line/10 bg-surface p-3">
      <div className="flex flex-wrap items-end gap-3">
        <label className="min-w-64 flex-1 text-sm">
          <span className="font-medium text-fg-muted">Module title</span>
          <input
            value={title}
            onChange={(e) => onTitle(e.target.value)}
            className={SHARED_INPUT_CLASS}
          />
        </label>
        {graded ? (
          <label className="w-28 text-sm">
            <span className="font-medium text-fg-muted">Pass mark %</span>
            <input
              type="number"
              min={0}
              max={100}
              value={passMark}
              onChange={(e) =>
                onPassMark(Math.min(100, Math.max(0, Math.floor(Number(e.target.value) || 0))))
              }
              className={SHARED_INPUT_CLASS}
            />
          </label>
        ) : null}
      </div>
      <label className="mt-3 block text-sm">
        <span className="font-medium text-fg-muted">Description</span>
        <textarea
          rows={2}
          value={description}
          onChange={(e) => onDescription(e.target.value)}
          className={SHARED_INPUT_CLASS}
        />
      </label>
      <fieldset className="mt-3 text-sm">
        <legend className="font-medium text-fg-muted">What this module is</legend>
        <div className="mt-1.5 space-y-1.5">
          <label className="flex items-start gap-2 text-fg">
            <input
              type="radio"
              name="graded"
              className="mt-0.5"
              checked={graded}
              onChange={() => onGraded(true)}
            />
            <span>
              <span className="font-medium">Training, marked.</span>{' '}
              <span className="text-fg-muted">
                Questions have correct answers, the learner gets a score against the pass mark, and
                a certificate is issued on a pass.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-fg">
            <input
              type="radio"
              name="graded"
              className="mt-0.5"
              checked={!graded}
              onChange={() => onGraded(false)}
            />
            <span>
              <span className="font-medium">Assessment, not marked.</span>{' '}
              <span className="text-fg-muted">
                Answers are recorded rather than marked (DSE and the like). No answer key, no score
                and no certificate; completing it is the outcome.
              </span>
            </span>
          </label>
        </div>
      </fieldset>
      {graded ? (
        <fieldset className="mt-3 text-sm">
          <legend className="font-medium text-fg-muted">If they get questions wrong</legend>
          <div className="mt-1.5 space-y-1.5">
            <label className="flex items-start gap-2 text-fg">
              <input
                type="radio"
                name="retry-mode"
                className="mt-0.5"
                checked={retryMode === 'full_retake'}
                onChange={() => onRetryMode('full_retake')}
              />
              <span>
                <span className="font-medium">Full retake.</span>{' '}
                <span className="text-fg-muted">
                  Answers are marked at the end. A fail shows the score only (never which questions
                  were wrong) and they restart the whole module from the first slide.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2 text-fg">
              <input
                type="radio"
                name="retry-mode"
                className="mt-0.5"
                disabled={hasQuestionGroups}
                checked={retryMode === 'try_again'}
                onChange={() => onRetryMode('try_again')}
              />
              <span>
                <span className="font-medium">Try again per question.</span>{' '}
                <span className="text-fg-muted">
                  Each answer is marked immediately and they retry it until correct. Every try is
                  recorded; the reported score is first-try accuracy.
                  {hasQuestionGroups
                    ? ' Not available while the module has a question grid.'
                    : ''}
                </span>
              </span>
            </label>
          </div>
        </fieldset>
      ) : null}
      <fieldset className="mt-3 text-sm">
        <legend className="font-medium text-fg-muted">Question bank</legend>
        <div className="mt-1.5 space-y-1.5">
          <label className="flex items-center gap-2 text-fg">
            <input
              type="checkbox"
              checked={subsetOn}
              disabled={questionCount === 0 || bankBlocked}
              onChange={(e) =>
                onServeQuestionCount(e.target.checked ? Math.max(1, questionCount) : null)
              }
            />
            <span>Draw a random subset of questions each attempt</span>
          </label>
          {subsetOn ? (
            <div className="flex items-center gap-2 pl-6 text-fg-muted">
              <span>Serve</span>
              <input
                type="number"
                min={1}
                max={Math.max(1, questionCount)}
                value={serveQuestionCount ?? 1}
                onChange={(e) =>
                  onServeQuestionCount(
                    Math.min(
                      Math.max(1, questionCount),
                      Math.max(1, Math.floor(Number(e.target.value) || 1)),
                    ),
                  )
                }
                className={`${SHARED_INPUT_CLASS} w-20`}
              />
              <span>of {questionCount} questions</span>
            </div>
          ) : null}
          <label className="flex items-center gap-2 text-fg">
            <input
              type="checkbox"
              checked={shuffleQuestions}
              disabled={bankBlocked}
              onChange={(e) => onShuffleQuestions(e.target.checked)}
            />
            <span>Shuffle the order questions are shown</span>
          </label>
          {bankBlocked ? (
            <p className="pl-6 text-xs text-fg-dim">
              A question grid holds several questions on one page, so a random subset or shuffle
              cannot be drawn from it.
            </p>
          ) : null}
        </div>
      </fieldset>
    </div>
  );
}
